import React, { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { DashboardProvider } from './context/DashboardContext'
import { LocationsProvider } from './context/LocationsContext'
import { ReviewsProvider } from './context/ReviewsContext'
import { ErrorBoundary } from './components/ui/error-boundary'
import { LoadingPage } from './components/ui/loading-spinner'
import ProtectedRoute, { PublicOnlyRoute } from './components/auth/ProtectedRoute'

// Lazy load pages for better performance
const LandingPage = React.lazy(() => import('./pages/LandingPage'))
const LoginPage = React.lazy(() => import('./pages/LoginPage'))
const SignupPage = React.lazy(() => import('./pages/SignupPage'))
const SignupSuccessPage = React.lazy(() => import('./pages/SignupSuccessPage'))
const EmailVerificationPage = React.lazy(() => import('./pages/EmailVerificationPage'))
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'))
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'))
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'))
const BusinessPage = React.lazy(() => import('./pages/BusinessPage'))
const SupportPage = React.lazy(() => import('./pages/SupportPage'))

// Loading component for Suspense
const PageLoader = () => <LoadingPage />

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LocationsProvider>
          <ReviewsProvider>
            <DashboardProvider>
              <Router>
            <div className="App">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                
                {/* Auth routes - only accessible when not authenticated */}
                <Route 
                  path="/login" 
                  element={
                    <PublicOnlyRoute>
                      <LoginPage />
                    </PublicOnlyRoute>
                  } 
                />
                <Route
                  path="/signup"
                  element={
                    <PublicOnlyRoute>
                      <SignupPage />
                    </PublicOnlyRoute>
                  }
                />
                
                {/* Signup success route - public access */}
                <Route
                  path="/signup-success"
                  element={<SignupSuccessPage />}
                />
                
                {/* Email verification route - public access */}
                <Route
                  path="/verify-email"
                  element={<EmailVerificationPage />}
                />
                
                {/* Protected routes - require authentication */}
                <Route
                  path="/dashboard/*"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                
                <Route 
                  path="/onboarding" 
                  element={
                    <ProtectedRoute>
                      <OnboardingPage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/business"
                  element={
                    <ProtectedRoute>
                      <BusinessPage />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/support"
                  element={
                    <ProtectedRoute>
                      <SupportPage />
                    </ProtectedRoute>
                  }
                />
                
                {/* OAuth callback route */}
                <Route 
                  path="/auth/callback" 
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <OAuthCallback />
                    </Suspense>
                  } 
                />
                
                {/* Catch all route - redirect to dashboard if authenticated, otherwise to landing */}
                <Route 
                  path="*" 
                  element={<Navigate to="/" replace />} 
                />
              </Routes>
            </Suspense>
            
            {/* Debug Panel - only show in development or when debug=true */}
              </div>
            </Router>
          </DashboardProvider>
        </ReviewsProvider>
      </LocationsProvider>
    </AuthProvider>
  </ErrorBoundary>
  )
}

// OAuth callback handler component
const OAuthCallback = () => {
  const { handleOAuthCallback } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  React.useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(location.search)
        const code = urlParams.get('code')
        const state = urlParams.get('state')
        
        if (code) {
          const result = await handleOAuthCallback(code, state)
          
          // Check if user needs onboarding
          if (result.user && !result.user.onboarding_completed) {
            navigate('/onboarding')
          } else {
            // After OAuth completion, redirect to dashboard
            // The SmartRedirect component will handle business setup if needed
            navigate('/dashboard')
          }
        } else {
          // No code parameter, redirect to login with error
          navigate('/login?error=oauth_failed')
        }
      } catch (error) {
        console.error('OAuth callback error:', error)
        navigate('/login?error=oauth_failed')
      }
    }
    
    handleCallback()
  }, [handleOAuthCallback, navigate, location.search])
  
  return <LoadingPage message="Completing authentication..." />
}

export default App